import { Link } from '@tanstack/react-router';
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { OpsOverview, ReindexAllResult } from '@nucleus-commerce/api-client/platform';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardAction,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { SystemPanel } from '@/features/system';
import { canManageOrganizations } from '@/shared/session/roles';
import { session } from '@/shared/session/session';
import { Mono } from '@/shared/ui/mono';
import { StatCard } from '@/shared/ui/stat-card';
import { StatusDot } from '@/shared/ui/status-dot';

import { lagParts, outboxTone, queueTone, rollupTone } from '../api/health';
import { ReindexAllDialog } from '../components/reindex-all-dialog';
import { useOpsOverview } from '../hooks/use-ops-overview';

const head = 'h-10 text-xs font-medium';

function QueuesCard({ queues }: { queues: OpsOverview['queues'] }) {
  const { t } = useTranslation('operations');
  const format = useFormat();
  return (
    <Card
      className="gap-0 overflow-hidden py-0 lg:col-span-2"
      data-tour="platform.operations.queues"
    >
      <CardHeader className="border-b px-5 py-4">
        <CardTitle>{t('queues.title')}</CardTitle>
        <CardDescription>{t('queues.description')}</CardDescription>
      </CardHeader>
      <Table className="text-[13px]" aria-label={t('queues.title')}>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className={`${head} pl-5`}>{t('queues.columns.name')}</TableHead>
            {(['waiting', 'active', 'delayed', 'failed', 'completed'] as const).map((key) => (
              <TableHead key={key} className={`${head} text-right last:pr-5`}>
                {t(`queues.columns.${key}`)}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {queues.map((queue) => (
            <TableRow key={queue.name}>
              <TableCell className="py-2.5 pl-5">
                <StatusDot tone={queueTone(queue)}>
                  <Mono className="text-foreground">{queue.name}</Mono>
                </StatusDot>
              </TableCell>
              <TableCell className="py-2.5 text-right tabular-nums">
                {format.number(queue.waiting)}
              </TableCell>
              <TableCell className="py-2.5 text-right tabular-nums">
                {format.number(queue.active)}
              </TableCell>
              <TableCell className="py-2.5 text-right tabular-nums">
                {format.number(queue.delayed)}
              </TableCell>
              <TableCell
                className={`py-2.5 text-right tabular-nums ${queue.failed > 0 ? 'text-destructive font-medium' : ''}`}
              >
                {format.number(queue.failed)}
              </TableCell>
              <TableCell className="text-muted-foreground py-2.5 pr-5 text-right tabular-nums">
                {format.number(queue.completed)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function SearchCard({ search, canManage }: { search: OpsOverview['search']; canManage: boolean }) {
  const { t } = useTranslation('operations');
  const format = useFormat();
  const [queued, setQueued] = useState<ReindexAllResult | null>(null);
  const drifting = search.filter((index) => !index.inSync).length;
  return (
    <Card
      className="gap-0 overflow-hidden py-0 lg:col-span-3"
      data-tour="platform.operations.search"
    >
      <CardHeader className="border-b px-5 py-4">
        <CardTitle>{t('search.title')}</CardTitle>
        <CardDescription>
          {drifting > 0 ? t('search.drifting', { count: drifting }) : t('search.allInSync')}
        </CardDescription>
        {canManage ? (
          <CardAction data-tour="platform.operations.reindex-all">
            <ReindexAllDialog onQueued={setQueued} />
          </CardAction>
        ) : null}
      </CardHeader>
      {queued ? (
        <Alert role="status" className="rounded-none border-x-0 border-t-0">
          <AlertDescription>{t('search.queued', { count: queued.stores })}</AlertDescription>
        </Alert>
      ) : null}
      <Table className="text-[13px]" aria-label={t('search.title')}>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className={`${head} pl-5`}>{t('search.columns.store')}</TableHead>
            <TableHead className={`${head} text-right`}>{t('search.columns.documents')}</TableHead>
            <TableHead className={`${head} text-right`}>{t('search.columns.published')}</TableHead>
            <TableHead className={`${head} pr-5`}>{t('search.columns.state')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {search.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground h-20 text-center">
                {t('search.empty')}
              </TableCell>
            </TableRow>
          ) : (
            search.map((index) => (
              <TableRow key={index.storeId}>
                <TableCell className="py-2.5 pl-5">
                  <Link
                    to="/organizations/$organizationId"
                    params={{ organizationId: index.organizationId }}
                    className="font-medium hover:underline"
                  >
                    {index.storeName}
                  </Link>
                </TableCell>
                <TableCell className="py-2.5 text-right tabular-nums">
                  {format.number(index.documents)}
                </TableCell>
                <TableCell className="py-2.5 text-right tabular-nums">
                  {format.number(index.publishedProducts)}
                </TableCell>
                <TableCell className="py-2.5 pr-5">
                  <StatusDot tone={index.inSync ? 'success' : 'warning'}>
                    {index.inSync ? t('search.inSync') : t('search.outOfSync')}
                  </StatusDot>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Card>
  );
}

function Operations() {
  const { t } = useTranslation('operations');
  const format = useFormat();
  const { me } = session.useSession();
  const overview = useOpsOverview();
  const data = overview.data;
  const lag = data ? lagParts(data.outbox.lagSeconds) : null;

  return (
    <PageContent>
      <PageHeader
        title={<span className="font-serif text-[1.875rem] font-normal">{t('title')}</span>}
        description={
          overview.dataUpdatedAt
            ? t('updatedAt', { time: format.time(new Date(overview.dataUpdatedAt)) })
            : t('description')
        }
        actions={
          <Button
            variant="outline"
            size="sm"
            data-tour="platform.operations.refresh"
            disabled={overview.isFetching}
            onClick={() => void overview.refetch()}
          >
            <RefreshCw className={overview.isFetching ? 'animate-spin' : undefined} aria-hidden />
            {t('refresh')}
          </Button>
        }
      />
      {overview.isError ? (
        <div className="pb-4">
          <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
        </div>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-3" data-tour="platform.operations.stats">
        <StatCard
          label={t('outbox.lag')}
          value={lag ? t('outbox.lagValue', lag) : undefined}
          hint={
            data ? (
              <StatusDot tone={outboxTone(data.outbox)}>
                {t('outbox.pending', { count: data.outbox.pending })}
              </StatusDot>
            ) : undefined
          }
          emphasis
        />
        <StatCard
          label={t('webhooks.failed')}
          value={data ? format.number(data.webhooks.failedLast24h) : undefined}
          hint={
            data ? t('webhooks.disabled', { count: data.webhooks.disabledEndpoints }) : undefined
          }
        />
        <StatCard
          label={t('rollup.label')}
          value={
            data
              ? data.lastRollupAt
                ? format.relative(data.lastRollupAt)
                : t('rollup.never')
              : undefined
          }
          hint={
            data ? (
              <StatusDot tone={rollupTone(data.lastRollupAt, overview.dataUpdatedAt)}>
                {data.lastRollupAt ? format.utc(data.lastRollupAt) : t('rollup.neverHint')}
              </StatusDot>
            ) : undefined
          }
        />
      </div>
      <div className="grid gap-4 pt-4 lg:grid-cols-3">
        {data ? <QueuesCard queues={data.queues} /> : null}
        <SystemPanel />
        {data ? (
          <SearchCard search={data.search} canManage={canManageOrganizations(me?.user.role)} />
        ) : null}
      </div>
    </PageContent>
  );
}

export function OperationsPage() {
  return (
    <FeatureErrorBoundary name="operations">
      <Operations />
    </FeatureErrorBoundary>
  );
}
